// Layout Components
import { Header, Footer } from "@components/layout";

// Page Components
import {
  Home,
  Buy,
  Rent,
  Offplan,
  Area,
  Services,
  Projects,
  Developers,
  AboutUs,
  ContactUs,
  ListProperty,
  BlogList,
  BlogDetail,
  MarketReport,
  AreaDetail,
  DeveloperDetailPage,
  PropertyDetailPage,
  ProjectDetailPage,
  FeaturedPropertyDetailPage,
  TestimonialsPage,
} from "@components/pages";

// Utils and Context
import ScrollToTop from "@utils/ScrollToTop.jsx";
import theme from "@assets/styles/Theme";
import { ROUTES } from "@constants/routes"; // Import centralized routes

// External Libraries
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Helper component to use location hook inside Router
function Layout() {
  const location = useLocation();

  // List of static paths using centralized routes
  const staticPaths = [
    ROUTES.HOME,
    ROUTES.BUY,
    ROUTES.RENT,
    ROUTES.OFF_PLAN,
    ROUTES.AREAS,
    ROUTES.SERVICES,
    ROUTES.PROJECTS,
    ROUTES.DEVELOPERS,
    ROUTES.ABOUT_US,
    ROUTES.CONTACT_US,
    ROUTES.LIST_PROPERTY,
    ROUTES.BLOG_LIST,
    ROUTES.MARKET_REPORT,
    ROUTES.TESTIMONIALS,
  ];

  // List of dynamic paths prefixes using centralized routes
  const dynamicPathPrefixes = [
    ROUTES.BLOG_DETAIL().split(':')[0], // "/blog/"
    ROUTES.AREA_DETAIL().split(':')[0], // "/areadetail/"
    ROUTES.DEVELOPER_DETAIL().split(':')[0], // "/developerdetail/"
    ROUTES.PROPERTIES().split(':')[0], // "/propertydetail/"
    ROUTES.PROJECT_DETAIL().split(':')[0], // "/projectdetail/"
    ROUTES.FEATURED_PROPERTY_DETAIL().split(':')[0], // "/featureddetail/"
  ];

  // Check if current location is valid
  const isValidPath =
    staticPaths.includes(location.pathname) ||
    dynamicPathPrefixes.some((prefix) => location.pathname.startsWith(prefix));

  return (
    <>
      {isValidPath && <Header />}
      <Routes>
        {/* Static Routes */}
        <Route path={ROUTES.HOME} element={<Home />} />
        <Route path={ROUTES.BUY} element={<Buy />} />
        <Route path={ROUTES.RENT} element={<Rent />} />
        <Route path={ROUTES.OFF_PLAN} element={<Offplan />} />
        <Route path={ROUTES.AREAS} element={<Area />} />
        <Route path={ROUTES.SERVICES} element={<Services />} />
        <Route path={ROUTES.PROJECTS} element={<Projects />} />
        <Route path={ROUTES.DEVELOPERS} element={<Developers />} />
        <Route path={ROUTES.ABOUT_US} element={<AboutUs />} />
        <Route path={ROUTES.CONTACT_US} element={<ContactUs />} />
        <Route path={ROUTES.LIST_PROPERTY} element={<ListProperty />} />
        <Route path={ROUTES.BLOG_LIST} element={<BlogList />} />
        <Route path={ROUTES.MARKET_REPORT} element={<MarketReport />} />
        <Route path={ROUTES.TESTIMONIALS} element={<TestimonialsPage />} />
        
        {/* Dynamic Routes */}
        <Route path={ROUTES.BLOG_DETAIL()} element={<BlogDetail />} />
        <Route path={ROUTES.AREA_DETAIL()} element={<AreaDetail />} />
        <Route path={ROUTES.DEVELOPER_DETAIL()} element={<DeveloperDetailPage />} />
        <Route path={ROUTES.PROPERTIES()} element={<PropertyDetailPage />} />
        <Route path={ROUTES.PROJECT_DETAIL()} element={<ProjectDetailPage />} />
        <Route path={ROUTES.FEATURED_PROPERTY_DETAIL()} element={<FeaturedPropertyDetailPage />} />
        
        {/* Redirect unknown routes to home */}
        <Route path={ROUTES.NOT_FOUND} element={<Navigate to={ROUTES.HOME} replace />} />
      </Routes>
      {isValidPath && <Footer />}
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Layout />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </Router>
    </ThemeProvider>
  );
}

export default App;